<script lang="ts">
import { onMount } from "svelte";
import CardLink from "../components/card-link.svelte";
import type { CardLinkIconImageName } from "../components/card-link.svelte";
import ArrowForwardSvg from "../components/icons/arrow-forward-svg.svelte";
import ArrowRight from "../components/icons/arrow-right.svelte";
import SendSvg from "../components/icons/send-svg.svelte";
import FirstViewCta from "../components/sections/first-view-cta.svelte";
import FirstView from "../components/sections/first-view.svelte";
import StudioListCard from "../components/studio-list-card.svelte";
import type { PageData } from "./$types";

export let data: PageData;

let youtube1: HTMLElement;
let youtube2: HTMLElement;
let googleMap: HTMLIFrameElement;

onMount(() => {
	youtube1.innerHTML = '<lite-youtube videoid="XhlvH7zv8Cc"></lite-youtube>';
	youtube2.innerHTML = '<lite-youtube videoid="OvvUEG21ldQ"></lite-youtube>';

	if (data.studios.length !== 1 || !googleMap) return;

	const mapEmbedUrl = data.studios[0].mapEmbedUrl;
	const observer = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) {
					googleMap.src = mapEmbedUrl;
					observer.unobserve(googleMap);
				}
			}
		},
		{
			root: null,
			threshold: 0.1,
		},
	);

	observer.observe(googleMap);
});

type MenuItem = {
	href: string;
	title: string;
	description: string;
	iconImageName: CardLinkIconImageName;
};

const mainMenuItems: MenuItem[] = [
	{
		href: "/courses/",
		title: "コース",
		description:
			"Balanced Bodyピラティスインストラクター養成講座は、マット、リフォーマー、AI3D解剖学、動きの原則の4つのコースを受講します",
		iconImageName: "import_contacts",
	},
	{
		href: "/prices/",
		title: "料金",
		description: "4つのコースに対して、単科受講と一括プランから選べます",
		iconImageName: "currency_yen",
	},
	{
		href: "/schedule/",
		title: "スケジュール",
		description: "各コースの直近の開催スケジュールです",
		iconImageName: "calendar_month",
	},
];
</script>

<FirstView />

<div class="bg-bgGray px-6 pb-8 h-56 relative sm:hidden">
  <div class="bg-white absolute h-56 -top-2 left-0 right-0 py-8 px-6 rounded-t-[20px]">
    <FirstViewCta />
  </div>
</div>

<div class="bg-bgGray px-6">
  <div class="sm:w-[1132px] sm:mx-auto pt-2 pb-4 sm:pt-12 sm:pb-8">
    <h2 class="mb-2">
      Balanced Bodyについて
    </h2>
    <a href="/about/" class="text-primary underline flex items-center">
      <p>
        Balanced Bodyについて詳しく知る
      </p>
    </a>
    <p class="mt-8">
      本校で指導資格が取得できるBalanced Bodyのピラティスインストラクター養成講座は、基礎解剖学と運動学の学習から始まります。<br>
      <br class="sm:hidden">
      せっかく学ぶならきちんとした知識をもったピラティスインストラクターに。<br>
      <br class="sm:hidden">
      本校では対面での受講だけではなく、完全オンラインでも世界に通用するピラティスインストラクター資格が取得できます。
      <a
        href="/about/"
        class="inline-flex border border-solid border-action fill-action rounded-full py-1 px-2 mt-[2px] hover:bg-action hover:fill-white"
        aria-label="Balanced Bodyについて詳しく知る"
      >
        <ArrowForwardSvg size={14} />
      </a>
    </p>
  </div>
</div>

<!-- 主要リンク（コース、料金、スケジュール） -->
<div class="px-6 py-8 bg-bgGray">
  <div class="grid grid-cols-1 gap-4 sm:w-[1132px] sm:mx-auto sm:grid-cols-3 sm:gap-x-8">
    {#each mainMenuItems as { href, iconImageName, title, description }}
      <CardLink
        href={href}
        iconImageName={iconImageName}
        title={title}
        description={description}
        border={false}
        large={true}
      />
    {/each}
    <CardLink href="/otherschools/" title="ブリッジ制度" description="他の流派/団体の方でBalanced Body認定資格に興味のある方はブリッジ制度をご利用ください。" border={false} large={true} />
    <CardLink href="/followup/" title="フォローアップ" description="受講中や資格取得後も、様々な講座やレッスンで学びをサポートします。" border={false} large={true} />
    <!-- PC版のみ、おさまりが悪いのでもう1コンテンツ並べた -->
    <div class="hidden sm:block">
      <CardLink href="/faq/" title="よくあるご質問" description="資格取得までの流れやお支払いのタイミングなど、お問い合わせの多いご質問をまとめました。" border={false} large={true} />
    </div>
    <!-- スマホ版のみ、申し込みボタンを設置 -->
    <div class="bg-white p-4 sm:hidden">
      <div class="font-bold tracking-wide data-[large=true]:text-xl">
        お申し込み
      </div>
      <p class="mt-2 data-[large=true]:mt-4 text-textGray text-sm tracking-wide leading-6 mb-4">
        対面でもオンラインでも受講できます
      </p>
      <a
        href="/entry/"
        target="_blank"
        class="w-full min-h-12 pl-3 pr-1 py-2 flex items-center justify-between rounded-lg bg-primary text-white border-primary border-solid border-2 hover:opacity-70"
      >
        <div class="flex items-center gap-x-2">
          <SendSvg color="#FFF" />
          <div class="font-bold text-base">
            受講を申し込む
          </div>
        </div>
        <ArrowRight color="#FFF" />
      </a>
    </div>
  </div>
</div>

<!-- 紹介動画 -->
<div class="bg-bgGray pb-8">
  <div class="bg-white rounded-[20px] py-10 px-6 sm:w-[1132px] sm:mx-auto">
    <h2>
      紹介動画
    </h2>
    <div class="grid grid-cols-1 gap-y-8 sm:grid-cols-2 sm:gap-y-0 sm:gap-x-8">
      <div>
        <p class="mb-2">マットインストラクター養成講座の講座内容についてのご紹介</p>
        <div bind:this={youtube1} class="relative w-full pb-[56.25%] h-0 overflow-hidden" />
      </div>
      <div>
        <p class="mb-2">養成講座受講にあたって、講師からのメッセージ</p>
        <div bind:this={youtube2} class="relative w-full pb-[56.25%] h-0 overflow-hidden" />
      </div>
    </div>
    <div class="mt-10">
      <h2>
        世界で通用するマットピラティスインストラクターになりたい方へ
      </h2>
      <p>
        オンライン開講の養成コースで、ご自宅等でBalanced Body認定マットピラティスインストラクターの資格取得を目指せます。<br>
        <br>
        インストラクター資格は、あなたのスキルアップ・キャリアチェンジの大きな武器になります。ピラティスインストラクターは定年などの制限・条件に縛られることなく、お客様がいらっしゃれば長くあなたの強みとして活かせ、あなたのライフスタイルに合わせた働き方を実現することができる資格です。<br>
        ピラティスインストラクター資格には様々な団体やレベルがありますが、ぜひ世界で認められ、通用する資格を取得して、ご自身の未来の選択肢を広げてみませんか。<br>
        <br>
        また、最近ではインストラクターになることを目標にするだけではなく、あなた自身の健康な生活を、あなた自身でメンテナンスできるよう、自分の体の仕組みとその向き合い方を勉強したいという受講目的の方も増えてきています。自身の体をいたわりながら、今の仕事を長く、パフォーマンスを上げながら継続し、家族や友人に何かアドバイスを求められた時には周りの人たちに、認定インストラクターとしてアドバイスや指導を行うことができる。<br>
        そんなあなたのような方が増えてきています。<br>
        <br>
        ぜひこのページにたどり着けたことをきっかけに、なにを目的にインストラクター資格に興味をお持ちになったのか、改めて考えていただけると幸いです。
      </p>
      <p class="mt-8">
        <span class="text-lg">萩野 佳央理</span><br>
        Pilates StudioV8代表<br>
        Balanced Body認定マスターインストラクター
      </p>
    </div>
  </div>
</div>

<div class="bg-bgGray pb-8">
  <div class="grid grid-cols-1 gap-y-6 sm:w-[1132px] sm:mx-auto sm:grid sm:grid-cols-3 sm:gap-x-6 sm:gap-y-0">
    <!-- 講師 -->
    <div class="bg-white rounded-[20px] py-10 px-6 flex flex-col">
      <div>
        <h2>
          講師
        </h2>
        <h3 class="hidden sm:block mt-0 mb-4">
          萩野佳央理
        </h3>
        <div class="relative w-full h-64 sm:h-[260px] overflow-hidden rounded-lg">
          <img src="/lecturer-kaori.webp" loading="lazy" class="absolute top-1/4 left-1/2 transform -translate-x-1/2 -translate-y-1/4 w-full h-auto" alt="担当講師 萩野佳央理">
        </div>
        <h3 class="block sm:hidden mt-6 mb-2">
          萩野佳央理
        </h3>
      </div>
      <div class="flex-1 flex flex-col justify-between">
        <p class="mt-0 sm:mt-6">
          Balanced Body プリンシパル・エディケーター
        </p>
        <a href="/master-instructor/" class="mt-2 text-primary underline flex items-center">
          <p>
            詳しいプロフィールはこちら
          </p>
        </a>
      </div>
    </div>

    <!-- 受講会場 / スタジオ一覧 -->
    <div class="bg-white rounded-[20px] py-10 px-6 flex flex-col">
      {#if data.studios.length === 1}
        {@const studio = data.studios[0]}
        <div>
          <h2>
            受講会場
          </h2>
          <h3>
            ピラティススタジオV8
          </h3>
          <div class="h-[400px] sm:h-[260px]">
            <iframe
              bind:this={googleMap}
              title={studio.name}
              src=""
              width="100%"
              height="100%"
              style="border:0;"
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
        <div class="flex-1 flex flex-col justify-between">
          <address class="mt-6 tracking-wide leading-7 not-italic whitespace-pre-line">
            {studio.address}
          </address>
          <a href="/studios/{studio.id}/" class="mt-2 text-primary underline flex items-center">
            <p>
              アクセスを詳しく確認する
            </p>
          </a>
        </div>
      {:else}
        <div class="flex-1 flex flex-col justify-between">
          <div>
            <h2>
              スタジオ一覧
            </h2>
            <div class="grid grid-cols-1 gap-4 mt-4">
              {#each data.studios as studio}
                <StudioListCard
                  href="/studios/{studio.id}/"
                  name={studio.name}
                  area={studio.area}
                />
              {/each}
            </div>
          </div>
          <a href="/studios/" class="mt-6 text-primary underline flex items-center">
            <p>
              すべてのスタジオをみる
            </p>
          </a>
        </div>
      {/if}
    </div>

    <!-- お知らせ（PC版のみ、おさまりが悪いためトップに設置） -->
    <div class="hidden sm:flex bg-white rounded-[20px] py-10 px-6 flex-col justify-between">
      <div>
        <h2>
          お知らせ
        </h2>
        <div class="grid grid-cols-1 gap-y-4">
          {#each data.contents as { id, title }}
            <CardLink href="/news/{id}/" title={title} large={false} />
          {/each}
        </div>
      </div>
      <div>
        <a href="/news/" class="mt-2 text-primary underline flex items-center">
          <p>
            もっと見る
          </p>
        </a>
      </div>
    </div>

    <!-- スマホ版のみ、申し込みボタンを設置 -->
    <div class="bg-white py-10 px-6 sm:hidden">
      <h2>
        お申し込み
      </h2>
      <p class="mb-4 -mt-4">
        対面でもオンラインでも受講できます
      </p>
      <a
        href="/entry/"
        target="_blank"
        class="w-full min-h-12 pl-3 pr-1 py-2 flex items-center justify-between rounded-lg bg-primary text-white border-primary border-solid border-2 hover:opacity-70"
      >
        <div class="flex items-center gap-x-2">
          <SendSvg color="#FFF" />
          <div class="font-bold text-base">
            受講を申し込む
          </div>
        </div>
        <ArrowRight color="#FFF" />
      </a>
    </div>
  </div>
</div>